import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useLocation, Link as RouterLink, useNavigate } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Breadcrumbs, Typography, Link, Stack, IconButton, Tooltip } from '@mui/material'
import { transformStrToCapital } from 'utils/router'
import { capitalize } from 'utils/data-manipulation/valueFormatter'
import { UUIDRegex } from 'utils/constants'
import AddIcon from '@mui/icons-material/Add'
import SelectButton from 'components/generic/buttons/SelectButton'
import { useMediaQuery } from '@mui/material'
import { useSelector } from 'react-redux'
import LicenseScanModal from 'components/containers/customers/contacts/LicenseScanModal'
import { BadgeOutlined } from '@mui/icons-material'

const Crumb = ({ to, text }) => (
  <Link
    color="textSecondary"
    sx={{
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    }}
    component={RouterLink}
    to={to}
  >
    {text}
  </Link>
)

const RouterBreadcrumbs = ({ startAtPathIndex, accessibleMenuOptions, handleOptionClick }) => {
  const location = useLocation()
  const isMobile = useMediaQuery(query => query.breakpoints.down('sm'))
  const { useMinimalObt, enableScanLicenseShortcut } = useSelector(state => state.featureFlags?.variables || {})
  const navigate = useNavigate()
  const pathNamesArr = location.pathname.split('/')
  const pathNames = pathNamesArr.filter((x, idx) => x && idx > startAtPathIndex)
  const lastPath = pathNamesArr.slice(0, -1).join('/')
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false)

  const handleClick = option => {
    if (option && option.action) {
      handleOptionClick(option)
      navigate(option.action)
    }
  }

  const pathCrumbs = pathNames.map((value, index) => {
    const last = index === pathNames.length - 1
    const to = `/${pathNamesArr.slice(1, index + 4).join('/')}`
    let text = transformStrToCapital(decodeURIComponent(value)).split(' ').map(capitalize).join(' ')

    // If the value looks like an ID, replace it with "Details"
    if (UUIDRegex.test(value)) {
      text = ['ticket-library', 'outbound-tickets', 'ticket-history'].includes(pathNames[1])
        ? 'Ticket Details'
        : 'Details'
    }

    return last ? (
      <Typography color="text.primary" key={to}>
        {text}
      </Typography>
    ) : (
      <Crumb key={to} to={to} text={text} />
    )
  })

  if (pathNames[0] === 'commodities' && pathNames.length === 5) {
    pathCrumbs.splice(3, 1)
    const to = `/${pathNamesArr.slice(1, 6).join('/')}`
    const text = transformStrToCapital(decodeURIComponent(pathNames[2])).split(' ').map(capitalize).join(' ')
    pathCrumbs.splice(2, 1, <Crumb key={to} to={to} text={text} />)
  }

  return (
    <Stack sx={{ display: { xs: 'none', md: 'flex' } }} direction="row" alignItems="center">
      {pathNames.length > 1 && (
        <Link sx={{ display: 'flex', marginRight: '8px' }} color="text.primary" component={RouterLink} to={lastPath}>
          <ArrowBackIcon />
        </Link>
      )}

      <Breadcrumbs maxItems={2} aria-label="Breadcrumb">
        {pathCrumbs}
      </Breadcrumbs>

      {!isMobile && !useMinimalObt && (
        <>
          {accessibleMenuOptions?.length > 0 && (
            <SelectButton
              icon={<AddIcon />}
              tooltip="Create new (G)"
              variant="outlined"
              options={accessibleMenuOptions}
              onClick={handleClick}
              sx={{ ml: 2 }}
              size="small"
            />
          )}
          {enableScanLicenseShortcut && (
            <Tooltip title="Scan License">
              <IconButton onClick={() => setIsLicenseModalOpen(true)} sx={{ ml: 2 }} size="small">
                <BadgeOutlined />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}

      <LicenseScanModal isOpen={isLicenseModalOpen} onHideModal={() => setIsLicenseModalOpen(false)} />
    </Stack>
  )
}

RouterBreadcrumbs.propTypes = {
  startAtPathIndex: PropTypes.number,
  accessibleMenuOptions: PropTypes.array,
  handleOptionClick: PropTypes.func,
}

RouterBreadcrumbs.defaultProps = {
  startAtPathIndex: 2,
  accessibleMenuOptions: [],
  handleOptionClick: () => {},
}

Crumb.propTypes = {
  to: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
}

export default RouterBreadcrumbs
